import { AppChatRealtimeContext } from '../model/useAppChatRealtime';
import {
    useCallback,
    useEffect,
    type PropsWithChildren,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { ChatDialogResponse } from '@/core/generated/api';
import { useAuth } from '@/entities/Auth/store';
import { appChatService } from '@/entities/AppChat/api/appChatService';
import { useAppChatStore } from '@/entities/AppChat/model/store';
import { connectChatDialogRealtime } from '@/entities/ChatDialog/api/chatDialogRealtime';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { useChatDialogRealtime } from '@/features/chatDialogRealtime/model/useChatDialogRealtime';
import { useChatDialogNotifications } from '@/features/chatDialogRealtime/ui/useChatDialogNotifications';

/** Общая подписка поддержки приложения */
const connect = (callbacks: Parameters<typeof connectChatDialogRealtime>[0]) => (
    connectChatDialogRealtime(callbacks, appChatService)
);

/** Поддерживает уведомления и переписку приложения на всех страницах */
export const AppChatRealtimeProvider = ({ children }: PropsWithChildren) => {
    const canView = useCanAccessPermission('admin.chats.view');
    const sessionRevision = useAuth((state) => state.sessionRevision);
    const navigate = useNavigate();

    useEffect(() => () => useAppChatStore.getState().reset(), [canView, sessionRevision]);

    /** Открывает входящее сообщение приложения */
    const open = useCallback((dialog: ChatDialogResponse): void => {
        if (!dialog.id) return;
        void navigate({
            pathname: '/chats',
            search: new URLSearchParams({ dialogId: dialog.id }).toString()
        });
    }, [navigate]);

    const notify = useChatDialogNotifications(canView, open, useAppChatStore);
    const realtime = useChatDialogRealtime(canView, notify, useAppChatStore, connect);

    return <AppChatRealtimeContext.Provider value={realtime}>{children}</AppChatRealtimeContext.Provider>;
};
